import { Controller } from "@hotwired/stimulus"

// Handles per-row ranging status selection and save, review status, notes, and
// polling for background job completion after a ranging correction is saved.
export default class extends Controller {
  static values = {
    url: String,
    reviewUrl: String,
    noteUrl: String,
    pollUrl: String,
    stockId: Number,
    frame: String,
    pending: { type: Boolean, default: false }
  }
  static targets = ["btnI", "btnO", "btnC", "btnX", "saveButton", "noteButton", "noteForm", "noteInput"]

  connect() {
    this.currentValue = this.element.dataset.rangingRowCurrentValue || null
    this.selectedValue = null
    this.pollTimer = null

    // If row was rendered with pending=true, start polling immediately
    if (this.pendingValue) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  select({ params: { value } }) {
    if (value === this.currentValue && this.selectedValue === null) return
    if (value === this.selectedValue) {
      this.selectedValue = null
      this.updateButtons()
      return
    }
    this.selectedValue = value
    this.updateButtons()
  }

  save() {
    if (!this.selectedValue) return
    this.postAndReplace(this.urlValue, { stock_id: this.stockIdValue, value: this.selectedValue })
  }

  toggleApproval() {
    this.postAndReplace(this.reviewUrlValue, { stock_id: this.stockIdValue, status: "approved" })
  }

  toggleNote() {
    if (!this.hasNoteFormTarget) return
    const isHidden = this.noteFormTarget.classList.contains("hidden")
    this.noteFormTarget.classList.toggle("hidden", !isHidden)
    if (isHidden && this.hasNoteInputTarget) {
      this.noteInputTarget.focus()
    }
  }

  submitNote() {
    if (!this.hasNoteInputTarget) return
    const commentary = this.noteInputTarget.value.trim()
    if (!commentary) return
    this.postAndReplace(this.noteUrlValue, { stock_id: this.stockIdValue, commentary: commentary })
  }

  // Poll for background job completion
  startPolling() {
    this.stopPolling()
    this.pollTimer = setTimeout(() => this.poll(), 3000)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  async poll() {
    try {
      const response = await fetch(this.pollUrlValue, {
        headers: { "Accept": "text/vnd.turbo-stream.html" }
      })
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        // The row gets replaced. If still pending, the new row's controller
        // will start polling again via connect() + pendingValue check.
      }
    } catch (e) {
      // Network error -- retry after a longer delay
      this.pollTimer = setTimeout(() => this.poll(), 5000)
    }
  }

  updateButtons() {
    const displayValue = this.selectedValue || this.currentValue
    const buttons = { I: this.btnITarget, O: this.btnOTarget, C: this.btnCTarget, X: this.btnXTarget }
    const activeStyles = {
      I: "bg-green-100 ring-1 ring-green-500 font-black text-green-700",
      O: "bg-orange-100 ring-1 ring-orange-500 font-black text-orange-700",
      C: "bg-blue-100 ring-1 ring-blue-500 font-black text-blue-700",
      X: "bg-gray-200 ring-1 ring-gray-400 font-black text-gray-600"
    }
    const inactiveStyle = "bg-gray-100 text-gray-400 hover:bg-gray-200"
    const pendingStyles = {
      I: "bg-green-100 ring-1 ring-dashed ring-green-500 font-black text-green-700",
      O: "bg-orange-100 ring-1 ring-dashed ring-orange-500 font-black text-orange-700",
      C: "bg-blue-100 ring-1 ring-dashed ring-blue-500 font-black text-blue-700",
      X: "bg-gray-200 ring-1 ring-dashed ring-gray-400 font-black text-gray-600"
    }

    const allStyles = Object.values(activeStyles).join(" ").split(" ")
      .concat(Object.values(pendingStyles).join(" ").split(" "))
      .concat(inactiveStyle.split(" "))
    const uniqueStyles = [...new Set(allStyles)]

    for (const [val, btn] of Object.entries(buttons)) {
      btn.classList.remove(...uniqueStyles)
      if (val === displayValue) {
        const isPending = this.selectedValue && val === this.selectedValue && val !== this.currentValue
        const styles = isPending ? pendingStyles[val] : activeStyles[val]
        btn.classList.add(...styles.split(" "))
      } else {
        btn.classList.add(...inactiveStyle.split(" "))
      }
    }

    const hasUnsavedChange = this.selectedValue && this.selectedValue !== this.currentValue
    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.toggle("invisible", !hasUnsavedChange)
      this.saveButtonTarget.classList.toggle("pointer-events-none", !hasUnsavedChange)
    }
  }

  // POST via fetch and render the turbo_stream response directly
  async postAndReplace(url, fields) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new URLSearchParams()
    for (const [name, value] of Object.entries(fields)) {
      body.append(name, value)
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": csrfToken
        },
        body: body
      })
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (e) {
      console.error("Ranging review action failed:", e)
    }
  }
}
